import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CollapsibleHelpSection from '../../common/CollapsibleHelpSection';

type TppCardManagementHubProps = {
  totalPoolCards: number;
  onRegister: () => void;
  onImport: () => void;
  onSettings: () => void;
  onExport: () => void;
};

type CompactActionProps = {
  icon: string;
  label: string;
  title: string;
  variant: 'primary' | 'outline-secondary';
  onClick: () => void;
};

function CompactAction({ icon, label, title, variant, onClick }: CompactActionProps) {
  return (
    <Button
      size="sm"
      variant={variant}
      onClick={onClick}
      title={title}
      className="d-inline-flex align-items-center gap-1"
    >
      <i className={icon} aria-hidden />
      {label}
    </Button>
  );
}

/** カード管理ハブ: 登録・インポート・設定・Excel出力を1箇所に集約 */
export default function TppCardManagementHub({
  totalPoolCards,
  onRegister,
  onImport,
  onSettings,
  onExport,
}: TppCardManagementHubProps) {
  const { t } = useTranslation();

  return (
    <div className="mb-4 p-3 bg-white rounded shadow-sm">
      {totalPoolCards > 0 && (
        <p className="small text-muted mb-2">
          {t('tpp.card_management_pool_summary', { count: totalPoolCards })}
        </p>
      )}

      <div className="d-flex flex-wrap gap-2 mb-2">
        <CompactAction
          icon="fas fa-plus-circle"
          label={
            totalPoolCards === 0
              ? t('tpp.empty_register_cta')
              : t('tpp.register_card_button')
          }
          title={t('tpp.card_management_action_register_desc')}
          variant="primary"
          onClick={onRegister}
        />
        <CompactAction
          icon="fas fa-file-import"
          label={t('tpp.import_csv')}
          title={t('tpp.card_management_action_import_desc')}
          variant="outline-secondary"
          onClick={onImport}
        />
        <CompactAction
          icon="fas fa-cog"
          label={t('tpp.pool_settings_button')}
          title={t('tpp.card_management_action_settings_desc')}
          variant="outline-secondary"
          onClick={onSettings}
        />
        {totalPoolCards >= 1 && (
          <CompactAction
            icon="fas fa-file-excel"
            label={t('tpp.export_button')}
            title={t('tpp.card_management_action_export_desc')}
            variant="outline-secondary"
            onClick={onExport}
          />
        )}
      </div>

      <CollapsibleHelpSection
        titleKey="tpp.card_management_help_title"
        descriptionKey="tpp.card_management_help_body"
        stepsKey={[
          'tpp.card_management_help_step_register',
          'tpp.card_management_help_step_import',
          'tpp.card_management_help_step_settings',
          ...(totalPoolCards >= 1 ? ['tpp.card_management_help_step_export'] : []),
        ]}
        storageKey="tpp_card_management"
        defaultExpanded={false}
      />
    </div>
  );
}
